import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Plus, Trash2, Eye, Copy, Shield, AlertTriangle, AlertOctagon,
  User, Edit, FolderOpen, Search
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import AdminLayout from "./AdminLayout";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";

const RiskBadge = ({ level }) => {
  const config = {
    low: { bg: "bg-emerald-100", text: "text-emerald-700", icon: Shield, label: "Низкий" },
    medium: { bg: "bg-amber-100", text: "text-amber-700", icon: AlertTriangle, label: "Средний" },
    high: { bg: "bg-rose-100", text: "text-rose-700", icon: AlertOctagon, label: "Высокий" }
  };
  const c = config[level] || config.low;
  const Icon = c.icon;
  return (
    <Badge className={`${c.bg} ${c.text} border-0 text-xs`}>
      <Icon className="w-3 h-3 mr-1" />
      {c.label}
    </Badge>
  );
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
  toast.success("Скопировано");
};

const AdminArchiveView = ({ user }) => {
  const { archiveName } = useParams();
  const navigate = useNavigate();
  const [archive, setArchive] = useState(null);
  const [allAddresses, setAllAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialog, setAddDialog] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchData();
  }, [archiveName]);

  const fetchData = async () => {
    try {
      const [archRes, addrRes] = await Promise.all([
        axios.get(`${API}/admin/archives/${archiveName}`, { withCredentials: true }),
        axios.get(`${API}/admin/addresses`, { withCredentials: true })
      ]);
      setArchive(archRes.data);
      setAllAddresses(addrRes.data);
    } catch (error) {
      toast.error("Ошибка загрузки");
      navigate("/admin/archives");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToArchive = async () => {
    if (!selectedAddress) {
      toast.error("Выберите адрес");
      return;
    }
    try {
      await axios.post(`${API}/admin/archives/${archiveName}/add`, {
        address_id: selectedAddress
      }, { withCredentials: true });
      toast.success("Адрес добавлен");
      setAddDialog(false);
      setSelectedAddress("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка");
    }
  };

  const handleRemove = async (addressId) => {
    try {
      await axios.delete(`${API}/admin/archives/${archiveName}/${addressId}`, { withCredentials: true });
      toast.success("Удалено из архива");
      fetchData();
    } catch (error) {
      toast.error("Ошибка удаления");
    }
  };

  // Filter addresses not in this archive
  const archiveAddressIds = new Set(archive?.addresses?.map(a => a.address_id) || []);
  const availableAddresses = allAddresses.filter(a => !archiveAddressIds.has(a.address_id));

  // Filter displayed addresses by search
  const filteredAddresses = archive?.addresses?.filter(addr => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return addr.address?.toLowerCase().includes(q) || 
           addr.label?.toLowerCase().includes(q) ||
           addr.network?.toLowerCase().includes(q);
  }) || [];

  if (loading) {
    return (
      <AdminLayout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout user={user}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin/archives">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Назад
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{archiveName}</h1>
              <p className="text-slate-500">{archive?.addresses?.length || 0} адресов</p>
            </div>
          </div>
        </div>

        {/* Search and Add */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Поиск по адресу, метке..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button onClick={() => setAddDialog(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="w-4 h-4 mr-2" />
            Добавить адрес
          </Button>
        </div>

        {/* Addresses List */}
        {filteredAddresses.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">
                {searchQuery ? "Ничего не найдено" : "Архив пуст"}
              </p>
              <p className="text-sm text-slate-400 mt-1">
                {searchQuery ? "Попробуйте другой запрос" : "Добавьте адреса для отслеживания"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredAddresses.map((addr) => (
              <Card key={addr.address_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Badges Row */}
                      <div className="flex items-center gap-2 mb-2">
                        <RiskBadge level={addr.risk_level} />
                        <Badge className={`border-0 text-xs ${
                          (addr.trust_score || 0) >= 70 ? "bg-emerald-100 text-emerald-700" :
                          (addr.trust_score || 0) >= 40 ? "bg-amber-100 text-amber-700" : 
                          "bg-rose-100 text-rose-700"
                        }`}>
                          Доверие: {addr.trust_score || 0}/100
                        </Badge>
                        {addr.is_blocked && (
                          <Badge className="bg-rose-100 text-rose-700 border-0 text-xs">
                            Заблокирован
                          </Badge>
                        )}
                      </div>
                      
                      {/* Address with Copy */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-mono text-base text-slate-900 truncate max-w-[400px]">
                          {addr.address}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(addr.address)}
                          className="p-1 h-7"
                        >
                          <Copy className="w-4 h-4 text-slate-400" />
                        </Button>
                      </div>

                      {/* Info Row */}
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                        <span className="font-medium text-slate-700">{addr.label || 'Без метки'}</span>
                        <span>Баланс: <strong className="text-slate-900">${addr.total_balance?.toLocaleString() || 0}</strong></span>
                        <span>Сеть: {addr.network || 'ETH'}</span>
                        <span>Транзакций: {addr.trading_history?.length || 0}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Link to={`/address/${addr.address_id}`}>
                        <Button variant="outline" size="sm" title="Просмотр">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Link to={`/admin/addresses/${addr.address_id}/edit`}>
                        <Button variant="outline" size="sm" title="Редактировать транзакции">
                          <Edit className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemove(addr.address_id)}
                        className="text-rose-600 hover:bg-rose-50"
                        title="Удалить из архива"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add Dialog */}
        <Dialog open={addDialog} onOpenChange={setAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Добавить адрес в архив {archiveName}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Выберите адрес</Label>
                <Select value={selectedAddress} onValueChange={setSelectedAddress}>
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите адрес" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableAddresses.length === 0 ? (
                      <div className="p-3 text-sm text-slate-500 text-center">
                        Нет доступных адресов
                      </div>
                    ) : (
                      availableAddresses.map(a => (
                        <SelectItem key={a.address_id} value={a.address_id}>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs">{a.address.slice(0, 20)}...</span>
                            <span className="text-slate-500">({a.label || 'Без метки'})</span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddDialog(false)}>
                Отмена
              </Button>
              <Button 
                onClick={handleAddToArchive} 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={!selectedAddress}
              >
                Добавить
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminArchiveView;
